import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { useFormik } from "formik";
import * as yup from "yup";
import { AuthContext } from "../../../context/AuthContext";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import { LoadingButton } from "@mui/lab";

const CreateEmployeeForCompanyDialog = ({
  open,
  defaultFullName = "",
  handleClose,
  onSuccess,
  companyId,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { openSnackMessage } = useContext(SnackAlertContext);
  const { authToken, decoded, ezorder } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSignUp = async (value) => {
    try {
      setLoading(true);
      let user = {
        ...value,
        companyId: companyId,
      };

      const response = await ezorder.post(`/admin/auth/signup/employee`, user, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (response.status == 200 || response.status == 201) {
        onSuccess(response.data.user);
      } else {
        setErrorMessage(response.response.data.error);
      }
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        setErrorMessage(error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        setErrorMessage(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = yup.object().shape({
    fullName: yup.string().required("Name is required"),
    email: yup
      .string()
      .email("Please use a valid email")
      .required("Email is required"),
    phone: yup.string(),
    role: yup.string().required("Role is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: defaultFullName ?? "",
      email: "",
      phone: "",
      role: "USER",
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleSignUp(values);
    },
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (!loading) handleClose();
        }}
        scroll={"paper"}
        fullScreen={isSmallScreen}
        fullWidth
      >
        <DialogTitle>Add New User Account</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <TextField
              sx={{ marginTop: "8px" }}
              disabled={loading}
              label="Full Name *"
              id="fullName"
              name="fullName"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.fullName &&
                formik.errors.fullName &&
                Boolean(formik.errors.fullName && true)
              }
              helperText={
                formik.touched.fullName &&
                formik.errors.fullName &&
                `${formik.errors.fullName}`
              }
            />
            <TextField
              disabled={loading}
              label="Email *"
              id="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.email &&
                formik.errors.email &&
                Boolean(formik.errors.email && true)
              }
              helperText={
                formik.touched.email &&
                formik.errors.email &&
                `${formik.errors.email}`
              }
            />
            <TextField
              disabled={loading}
              label="Phone"
              id="phone"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.phone &&
                formik.errors.phone &&
                Boolean(formik.errors.phone && true)
              }
              helperText={
                formik.touched.phone &&
                formik.errors.phone &&
                `${formik.errors.phone}`
              }
            />
            <TextField
              select
              disabled={loading}
              label="Role *"
              id="role"
              name="role"
              value={formik.values.role}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.role &&
                formik.errors.role &&
                Boolean(formik.errors.role && true)
              }
              helperText={
                formik.touched.role &&
                formik.errors.role &&
                `${formik.errors.role}`
              }
            >
              <MenuItem value={"ADMIN"}>Admin</MenuItem>
              <MenuItem value={"USER"}>Basic User</MenuItem>
            </TextField>
          </Box>
          <Box>
            {errorMessage && (
              <Box>
                <Typography color={"red"}>{errorMessage}</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            sx={{}}
            loading={loading}
            variant="outlined"
            onClick={() => {
              if (!loading) handleClose();
            }}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            sx={{ marginLeft: "8px" }}
            loading={loading}
            variant="contained"
            onClick={() => formik.handleSubmit()}
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default CreateEmployeeForCompanyDialog;
